/**
 * Confidentialite — page publique RGPD.
 */
import { Link } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import WhatsAppFAB from "../components/WhatsAppFAB";

const LAST_UPDATE = "11 mai 2026";

export default function Confidentialite() {
  return (
    <div style={{
      background: colors.bg,
      color: colors.text,
      minHeight: "100vh",
      fontFamily: fonts.body,
    }}>
      <Navbar />

      <main style={{
        maxWidth: 820,
        margin: "0 auto",
        padding: "120px clamp(16px, 4vw, 24px) 80px",
      }}>
        <header style={{ marginBottom: 32 }}>
          <h1 style={{ fontFamily: fonts.heading, fontSize: "clamp(32px, 5vw, 44px)", fontWeight: 700, marginBottom: 8 }}>
            Politique de confidentialité
          </h1>
          <p style={{ color: colors.textMuted, fontSize: 14 }}>
            Dernière mise à jour : {LAST_UPDATE}
          </p>
        </header>

        <Section title="1. Qui sommes-nous">
          <p>
            <strong>Mon Ministère</strong> est un service SaaS de gestion d'église
            édité et accessible depuis{" "}
            <a href="https://mon-ministere.com" style={linkStyle}>mon-ministere.com</a>.
            Pour toute question relative à vos données personnelles, vous pouvez nous
            joindre à l'adresse{" "}
            <a href="mailto:contact@mon-ministere.com" style={linkStyle}>contact@mon-ministere.com</a>.
          </p>
        </Section>

        <Section title="2. Données collectées">
          <p>Nous collectons strictement les données nécessaires au bon fonctionnement du service :</p>
          <ul style={listStyle}>
            <li><strong>Compte :</strong> nom complet, adresse email, mot de passe haché (bcrypt), téléphone (facultatif), photo de profil (facultatif), titre (Pasteur, Diacre…), biographie, date de naissance, adresse postale (toutes facultatives).</li>
            <li><strong>Église :</strong> nom, ville, pays, contacts associés.</li>
            <li><strong>Adresse IP :</strong> utilisée <em>uniquement</em> pour détecter votre pays via le service tiers <a href="https://ipapi.co" target="_blank" rel="noreferrer" style={linkStyle}>ipapi.co</a> et afficher les prix dans la bonne devise. L'IP n'est pas stockée par Mon Ministère.</li>
            <li><strong>Cookies de session :</strong> jeton JWT (access + refresh) stockés en <code>localStorage</code> pour vous garder connecté. Préférence de devise (<code>mm_devise</code>).</li>
            <li><strong>Google Analytics 4 :</strong> mesure d'audience anonymisée (<code>anonymize_ip: true</code>). Identifiant de mesure <code>G-T29Q9N2L56</code>.</li>
          </ul>
        </Section>

        <Section title="3. Comment nous utilisons vos données">
          <ul style={listStyle}>
            <li>Authentifier votre compte et sécuriser l'accès à votre espace.</li>
            <li>Afficher les fonctionnalités adaptées à votre rôle (pasteur, fidèle).</li>
            <li>Adapter automatiquement les tarifs à votre devise locale.</li>
            <li>Améliorer le service grâce à des statistiques agrégées et anonymisées.</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            Vos données ne sont <strong>jamais vendues</strong> à des tiers et ne sont
            partagées qu'avec les sous-traitants techniques nécessaires (hébergement,
            détection géographique, analytics).
          </p>
        </Section>

        <Section title="4. Sous-traitants et services tiers">
          <ul style={listStyle}>
            <li><strong>Neon</strong> (hébergeur PostgreSQL) — UE/USA.</li>
            <li><strong>Railway</strong> (hébergeur backend) — USA.</li>
            <li><strong>Vercel</strong> (hébergeur frontend) — USA.</li>
            <li><strong>ipapi.co</strong> (détection pays via IP) — USA.</li>
            <li><strong>Google Analytics 4</strong> (mesure d'audience) — USA, données anonymisées.</li>
          </ul>
        </Section>

        <Section title="5. Durée de conservation">
          <ul style={listStyle}>
            <li>Données de compte : conservées tant que votre compte est actif. Supprimées sous 30 jours après une demande de suppression.</li>
            <li>Logs techniques : 90 jours maximum.</li>
            <li>Cookies de session : invalidés à la déconnexion ou après 7 jours d'inactivité (refresh token).</li>
          </ul>
        </Section>

        <Section title="6. Vos droits">
          <p>Conformément au RGPD (UE) et aux législations équivalentes, vous disposez à tout moment des droits suivants :</p>
          <ul style={listStyle}>
            <li><strong>Accès</strong> à l'ensemble des données vous concernant.</li>
            <li><strong>Rectification</strong> de toute information inexacte depuis votre profil.</li>
            <li><strong>Suppression</strong> (« droit à l'oubli ») de votre compte et de toutes les données associées.</li>
            <li><strong>Portabilité</strong> : récupération de vos données dans un format structuré (CSV, JSON).</li>
            <li><strong>Opposition</strong> au traitement à des fins analytiques (Google Analytics, ipapi.co).</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            Pour exercer un de ces droits, écrivez-nous à{" "}
            <a href="mailto:contact@mon-ministere.com" style={linkStyle}>contact@mon-ministere.com</a>.
            Une réponse vous parviendra sous 30 jours maximum.
          </p>
        </Section>

        <Section title="7. Sécurité">
          <p>
            Toutes les communications sont chiffrées en HTTPS. Les mots de passe sont
            hachés avec bcrypt (salt unique). Les tokens d'authentification expirent
            automatiquement (15 minutes pour l'access token, 7 jours pour le refresh).
          </p>
        </Section>

        <Section title="8. Modifications">
          <p>
            Cette politique peut évoluer. La date de dernière mise à jour est indiquée
            en haut de page. En cas de modification substantielle, les utilisateurs
            connectés en sont informés par email.
          </p>
        </Section>

        <div style={{
          marginTop: 40,
          padding: 20,
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          textAlign: "center",
        }}>
          <p style={{ marginBottom: 12, color: colors.textMuted, fontSize: 14 }}>
            Une question ? Un doute ?
          </p>
          <a
            href="mailto:contact@mon-ministere.com"
            style={{
              color: colors.gold,
              fontWeight: 600,
              fontSize: 15,
              textDecoration: "none",
            }}
          >
            ✉ contact@mon-ministere.com
          </a>
        </div>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link to="/" style={{ color: colors.textMuted, fontSize: 13, textDecoration: "none" }}>
            ← Retour à l'accueil
          </Link>
        </div>
      </main>

      <Footer />
      <WhatsAppFAB />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontFamily: fonts.heading, fontSize: 22, fontWeight: 700, marginBottom: 12, color: colors.gold }}>
        {title}
      </h2>
      <div style={{ color: colors.text, fontSize: 14, lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  );
}

const linkStyle = {
  color: colors.gold,
  textDecoration: "underline",
};

const listStyle = {
  paddingLeft: 20,
  margin: "8px 0 0",
  color: colors.text,
  lineHeight: 1.7,
};
